import { useState } from 'react';
import Required from '../../../common-components/Required';
import CloseButton from '../../../custom-components/CloseButton';
import CancelButton from '../../../custom-components/CancelButton';
import BlackButton from '../../../custom-components/BlackButton';
import FileUploader from '../../../common-components/FileUploader';
import { useDispatch, useSelector } from 'react-redux';
import {
  fileDelete,
  fileUpload,
  multipleFileUpload,
  previewImage,
  progressPercentage,
  resetMediaErrorAlert,
  resetMultipleFileUpload,
  setSelectedFile,
} from '../../../../slices/FileUploadSlice';
import { RecreationStringLabels } from '../../../../util/Strings';
import {
  setPreThumbnailId,
  setPrevRecreationIds,
  setPrevRecreationImageDetails,
  setPrevThumbnailDetails,
} from '../../../../slices/RecreationSlice';
import ImagePreviewWithFilename from '../../../common-components/ImagePreviewWithFilename';
import { NewRecreationFormValidations } from '../../../../util/Validations';
import ErrorMessageComponent from '../../../common-components/ErrorMessageComponent';
import useFileValidation from '../../../../util/useFileValidation';
import Select from 'react-select';
import ErrorAlert from '../../../common-components/ErrorAlert';
import ErrorMessages from '../../../../util/ErrorMessages';

function AddNewRecreationForm({ show, onClickNext, data }) {
  const dispatch = useDispatch();

  // initialize arrays to [] instead of null to avoid null checks everywhere
  const initialRecreationPhotoIds = data?.recreationPhotoIds ?? [];
  const multipleFileNameWithImageUrls = [];
  if (data && data.photoUrls && data.photoFileNames) {
    for (let i = 0; i < data.photoUrls.length; i++) {
      multipleFileNameWithImageUrls.push({
        photoUrl: data.photoUrls[i],
        fileName: data.photoFileNames[i],
        id: data.recreationPhotoIds[i],
      });
    }
  }

  const [state, setState] = useState({
    name: data?.name ?? null,
    filter: data?.filter ?? null,
    area: data?.area ?? null,
    openingTime: data?.openingTime ?? null,
    closingTime: data?.closingTime ?? null,
    closedDays: data?.closedDays ?? ['Sunday'],
    address: data?.address ?? null,
    latitude: data?.latitude ?? null,
    longitude: data?.longitude ?? null,
    description: data?.description ?? null,
    recreationThumbnailId: data?.recreationThumbnailId ?? null,
    recreationPhotoIds: initialRecreationPhotoIds,
  });

  const [prevFileData, setPreFileData] = useState({
    photoUrl: data?.thumbnailUrl ?? null,
    fileName: data?.thumbnailFileName ?? null,
  });

  const [prevFileId, setPrevFileId] = useState(data?.recreationThumbnailId ?? null);
  const [prevMultipleFileIds, setPrevMultipleFileIds] = useState(initialRecreationPhotoIds);
  const [prevMultiFileData, setPrevMultiFileData] = useState(multipleFileNameWithImageUrls ?? []);
  const [imageDeleteList, setImageDeleteList] = useState([]);
  const [validation, setValidation] = useState({});

  const prevThumbnailId = useSelector((s) => s.recreation.prevThumbnailId);
  const prevRecreationIds = useSelector((s) => s.recreation.prevRecreationIds);
  const errorMessageCode = useSelector((s) => s.upload.errorCode);
  const prevRecreationThumbnailFileData = useSelector((s) => s.recreation.prevThumbnailDetails);
  const prevRecreationMultipleFileData = useSelector((s) => s.recreation.prevRecreationImageDetails);

  const {
    errorSingleMessage,
    setErrorSingleMessage,
    errorMultiMessage,
    setErrorMultiMessage,
    validateFile,
    validateFiles,
  } = useFileValidation();

  const roamTypeOptions = [
    { value: 1, label: 'Night Life' },
    { value: 2, label: 'Shopping' },
    { value: 3, label: 'Nature' },
    { value: 4, label: 'Sightseeing' },
    {value : 5,label: 'Activities'}
  ];

  const closedDaysOption = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thrusday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' },
    { value: 8, label: 'Open all Days' },
  ];

  function handleCloseErrorAlert() {
    dispatch(resetMediaErrorAlert(false));
    // restore UI from prev details saved in the redux slice if available
    setPrevMultiFileData(prevRecreationMultipleFileData ?? multipleFileNameWithImageUrls);
    handleMultipleImageState(); // note: this call will use prevMultipleFileIds state logic
    dispatch(setSelectedFile(null));
    setPreFileData(prevRecreationThumbnailFileData ?? { photoUrl: data?.thumbnailUrl ?? null, fileName: data?.thumbnailFileName ?? null });
    handleState();
    dispatch(setSelectedFile(null));
    setImageDeleteList([]);
    onClickNext();
  }

  const checkValidation = () => {
    const { errors, isValid } = NewRecreationFormValidations(state);
    setValidation(errors);
    return isValid;
  };

  function handleChange(e) {
    const value = e.target.value;
    setState((prev) => ({
      ...prev,
      [e.target.name]: value,
    }));
  }

  function resetImage() {
    dispatch(progressPercentage(0));
    dispatch(setSelectedFile(null));
    dispatch(previewImage(null));
    dispatch(resetMultipleFileUpload());
  }

  // ======= Handlers with safer updates =======
  function handleImageCancel(photoId) {
    // remove preview object safely
    setPrevMultiFileData((prev) => (Array.isArray(prev) ? prev.filter((image) => image.id !== photoId) : prev));

    // remove id from prevMultipleFileIds and update form state
    setPrevMultipleFileIds((prevIds) => {
      const ids = Array.isArray(prevIds) ? prevIds.filter((id) => id !== photoId) : [];
      // update the form state recreationPhotoIds as well
      setState((prevState) => ({ ...prevState, recreationPhotoIds: ids }));
      return ids;
    });

    // add to delete list using functional updater to avoid stale closures
    setImageDeleteList((prev) => [...prev, photoId]);
  }

  function handleThumbnailCancel() {
    // clear thumbnail id in form state
    setState((prev) => ({ ...prev, recreationThumbnailId: null }));

    // do not mutate prevFileData — use setter
    setPreFileData({ photoUrl: null, fileName: null });

    // mark previous thumbnail id for deletion (if available)
    setPrevFileId((currentPrevFileId) => {
      if (currentPrevFileId) {
        setImageDeleteList((prev) => [...prev, currentPrevFileId]);
      }
      return null; // clear prevFileId
    });
  }

  async function handleNextButton() {
    if (checkValidation()) {
      if (
        state.name &&
        state.filter &&
        state.area &&
        state.openingTime &&
        state.closingTime &&
        state.closedDays &&
        state.address &&
        state.latitude &&
        state.longitude &&
        state.description
      ) {
        const newData = state;
        if (data) {
          // copy state values back to state (keeps shape you expect)
          setState({
            name: newData.name,
            description: newData.description,
            filter: newData.filter,
            address: newData.address,
            latitude: newData.latitude,
            longitude: newData.longitude,
            area: newData.area,
            openingTime: newData.openingTime,
            closingTime: newData.closingTime,
            closedDays: newData.closedDays,
            photo: newData.photo,
            recreationThumbnailId: newData.recreationThumbnailId,
            recreationPhotoIds: newData.recreationPhotoIds,
          });

          // dispatch delete requests for images in the delete list
          if (imageDeleteList && imageDeleteList.length) {
            await Promise.all(
              imageDeleteList.map((imageId) =>
                dispatch(fileDelete({ id: imageId, isVideo: false, isMulti: true })).unwrap()
              )
            );
          }

          resetImage();

          if (onClickNext) {
            await onClickNext(newData, data._id);
          }

          // if thumbnail changed from previous stored thumbnail in redux, delete old thumbnail
          if (state.recreationThumbnailId !== prevThumbnailId && prevThumbnailId !== '') {
            const id = { id: prevThumbnailId, isVideo: false };
            await dispatch(fileDelete(id)).unwrap();
            dispatch(setPreThumbnailId(''));
          }

          // reset preview data to original data after submission
          setPreFileData({
            photoUrl: data?.thumbnailUrl ?? null,
            fileName: data?.thumbnailFileName ?? null,
          });

          setImageDeleteList([]);
          setErrorSingleMessage('');
          setErrorMultiMessage('');
        } else {
          // New record flow
          setState({});
          resetImage();
          setImageDeleteList([]);
          if (onClickNext) {
            await onClickNext(newData);
          }
        }
      } else {
        if (onClickNext) {
          await onClickNext();
        }
      }
    }
  }

  function handleMultipleImageState(fileId) {
    // Remove fileId from current data's recreationPhotoIds (use prevMultipleFileIds)
    if (fileId === undefined) {
      // No fileId provided — this call is used in handleCloseErrorAlert; just ensure state is synced to prevMultipleFileIds
      setState((prevState) => ({ ...prevState, recreationPhotoIds: prevMultipleFileIds }));
      return;
    }
    setPrevMultipleFileIds((prevIds) => {
      const ids = Array.isArray(prevIds) ? prevIds.filter((id) => id !== fileId) : [];
      setState((prevState) => ({ ...prevState, recreationPhotoIds: ids }));
      return ids;
    });
    setPrevMultiFileData((prev) => (Array.isArray(prev) ? prev.filter((img) => img.id !== fileId) : prev));
  }

  const handleFileSelect = (event) => {
    const files = event.target.files;
    postFileData(files);
  };

  const handleMultiFileSelect = (event) => {
    const files = event.target.files;
    postMultipleFileData(files);
  };

  const handleDrag = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postFileData(files);
  };

  const handleMultiDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postMultipleFileData(files);
  };

  function handleState() {
    // keep recreationThumbnailId in sync with original data when needed
    setState((prev) => ({ ...prev, recreationThumbnailId: data?.recreationThumbnailId ?? null }));
  }

  function postMultipleFileData(files) {
    const valid = validateFiles(files);
    if (valid) {
      // store preview of existing multi-file details in redux before upload
      if (prevMultiFileData && prevMultiFileData.length) {
        dispatch(setPrevRecreationIds(prevMultipleFileIds));
        dispatch(setPrevRecreationImageDetails(prevMultiFileData));
      }

      dispatch(multipleFileUpload(files))
        .unwrap()
        .then((res) => {
          const newIds = res.map((r) => r._id);
          setPrevMultipleFileIds((prevIds) => {
            const ids = Array.isArray(prevIds) ? prevIds.concat(newIds) : newIds;
            setState((prevState) => ({ ...prevState, recreationPhotoIds: ids }));
            return ids;
          });
        })
        .catch((err) => {
          // optional: set error messages if needed
          console.debug('multipleFileUpload failed', err);
        });
    }
  }

  function postFileData(files) {
    const valid = validateFile(files);
    if (valid) {
      const file = files[0];
      if (prevFileData && (prevFileData.photoUrl || prevFileData.fileName)) {
        dispatch(setPreThumbnailId(prevFileId));
        setPrevFileId((curr) => curr); // keep same prevFileId as reference
        dispatch(setPrevThumbnailDetails(prevFileData));
        // clear preview data object before uploading new file
        setPreFileData({ photoUrl: null, fileName: null });
      }
      if (file) {
        dispatch(setSelectedFile(file));
        dispatch(fileUpload(file))
          .unwrap()
          .then((res) => {
            // backend returns an array; take first id as before
            setState((prevState) => ({ ...prevState, recreationThumbnailId: res[0]._id }));
            setPrevFileId(res[0]._id);
          })
          .catch((err) => {
            console.debug('fileUpload failed', err);
          });
      }
    }
  }

  if (errorMessageCode) {
    return (
      <ErrorAlert
        show={errorMessageCode}
        onClickHandle={handleCloseErrorAlert}
        title="Error"
        subTitle={ErrorMessages(errorMessageCode)}
      />
    );
  }

  if (!show) {
    return null;
  }

  return (
    <div className="modal">
      <div className="form-scroll">
        <div className="form-flex">
          <div className="user-form-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <div className="header">
                {data ? RecreationStringLabels.formTitleUpdate : RecreationStringLabels.formTitleAdd}
              </div>
              <CloseButton
                onClickhandle={() => {
                  onClickNext();
                  if (data === null) {
                    setState({});
                    setValidation({});
                  }
                  setPreFileData({
                    photoUrl: data?.thumbnailUrl ?? null,
                    fileName: data?.thumbnailFileName ?? null,
                  });
                  setPrevMultipleFileIds(data?.recreationPhotoIds ?? []);
                  setPrevMultiFileData(multipleFileNameWithImageUrls);
                  setImageDeleteList([]);
                  setErrorSingleMessage('');
                  setErrorMultiMessage('');
                  resetImage();
                }}
              />
            </div>

            <div className="form-container">
              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.name}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    name="name"
                    value={state.name ?? ''}
                    onChange={(e) => handleChange(e)}
                    className="form-input"
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.name} />
              </div>

              <div className="form-label-input-select">
                <label className="form-label">
                  {RecreationStringLabels.filter}
                  <Required />
                </label>
                <Select
                  options={roamTypeOptions}
                  className="basic-single"
                  classNamePrefix="select"
                  name="eventType"
                  defaultValue={
                    data
                      ? roamTypeOptions.filter((event) => event.label === data.filter)
                      : undefined
                  }
                  closeMenuOnSelect={true}
                  onChange={(e) => {
                    setState((prev) => ({ ...prev, filter: e.label }));
                  }}
                />
                <ErrorMessageComponent message={validation.filter} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.description}
                  <Required />
                </label>
                <div className="user-form-input-container">
                  <textarea
                    className="form-input"
                    type="text"
                    name="description"
                    value={state.description ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                    rows={3}
                  />
                </div>
                <ErrorMessageComponent message={validation.description} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.address}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="address"
                    value={state.address ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.address} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.area}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="area"
                    value={state.area ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.area} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.latitude}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="latitude"
                    value={state.latitude ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="e.g. 12.9845"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.latitude} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.longitude}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="longitude"
                    value={state.longitude ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="e.g. 77.5846"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.longitude} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.openingTime}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="time"
                    value={state.openingTime ?? ''}
                    name="openingTime"
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.openingTime} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RecreationStringLabels.closingTime}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="time"
                    value={state.closingTime ?? ''}
                    name="closingTime"
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.closingTime} />
              </div>

              <div className="form-label-input-select">
                <label className="form-label">
                  {RecreationStringLabels.closedDays}
                  <Required />
                </label>
                <Select
                  options={closedDaysOption}
                  className="basic-multi-select"
                  isMulti
                  classNamePrefix="select"
                  name="closedDays"
                  closeMenuOnSelect={false}
                  defaultValue={
                    data
                      ? data.closedDays.map((closedDay) => {
                          const c = closedDaysOption.filter((day) => day.label === closedDay);
                          return c[0];
                        })
                      : [{ value: 7, label: 'Sunday' }]
                  }
                  onChange={(e) => {
                    const daysList = e.map((day) => day.label);
                    setState((prev) => ({ ...prev, closedDays: daysList }));
                  }}
                />
                <ErrorMessageComponent message={validation.closedDays} />
              </div>

              <FileUploader
                previewShape="rectangle"
                headerName={RecreationStringLabels.thumbnail}
                handleDragOver={handleDrag}
                handleDrop={handleDrop}
                handleFileSelect={handleFileSelect}
                setPreFileData={setPreFileData}
                handleState={handleState}
                type="recreation"
              />
              {errorSingleMessage !== '' && <p className="error image-error">{errorSingleMessage}</p>}
              {prevFileData?.photoUrl && data?.thumbnailUrl && data?.thumbnailFileName ? (
                <ImagePreviewWithFilename
                  photoUrl={data.thumbnailUrl}
                  fileName={data.thumbnailFileName}
                  handleCancel={handleThumbnailCancel}
                />
              ) : null}

              <FileUploader
                previewShape="rectangle"
                headerName={RecreationStringLabels.photos}
                handleDragOver={handleDrag}
                handleDrop={handleMultiDrop}
                handleFileSelect={handleMultiFileSelect}
                isMultiUpload={true}
                setMultipleFileData={setPrevMultiFileData}
                handleMultiState={handleMultipleImageState}
                type="recreation"
              />
              {errorMultiMessage !== '' && <p className="error image-error">{errorMultiMessage}</p>}
              {Array.isArray(prevMultiFileData) &&
                prevMultiFileData.length >= 1 &&
                prevMultiFileData.map((url) => {
                  return (
                    <ImagePreviewWithFilename
                      key={url.id}
                      photoUrl={url.photoUrl}
                      fileName={url.fileName}
                      photoId={url.id}
                      handleCancel={handleImageCancel}
                    />
                  );
                })}
            </div>

            <div className="footer-button-container">
              <CancelButton
                onClickhandle={() => {
                  onClickNext();
                  if (data === null) {
                    setState({});
                    setValidation({});
                  }
                  setPreFileData({
                    photoUrl: data?.thumbnailUrl ?? null,
                    fileName: data?.thumbnailFileName ?? null,
                  });
                  setPrevMultipleFileIds(data?.recreationPhotoIds ?? []);
                  setPrevMultiFileData(multipleFileNameWithImageUrls);
                  setImageDeleteList([]);
                  setErrorSingleMessage('');
                  setErrorMultiMessage('');
                  resetImage();
                }}
              >
                {RecreationStringLabels.cancel}
              </CancelButton>
                <BlackButton onClickhandle={() => handleNextButton()}>
                {data ? RecreationStringLabels.update : RecreationStringLabels.add}
              </BlackButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddNewRecreationForm;